import { useLayoutEffect, type RefObject } from 'react';

/** Fill the remaining scrollport without assuming the host header or sidebar size. */
export const useFirstRunViewportHeight = (formRef: RefObject<HTMLFormElement | null>) => {
  useLayoutEffect(() => {
    const form = formRef.current;
    if (!form) return;

    let scrollport: HTMLElement | null = null;
    for (let parent = form.parentElement; parent; parent = parent.parentElement) {
      if (/auto|scroll/.test(getComputedStyle(parent).overflowY)) {
        scrollport = parent;
        break;
      }
    }

    const update = () => {
      const bottom = scrollport
        ? scrollport.getBoundingClientRect().top + scrollport.clientHeight
        : window.innerHeight;
      const scrollTop = scrollport?.scrollTop ?? window.scrollY;
      const formBox = form.getBoundingClientRect();
      const height = Math.max(0, bottom - formBox.top - scrollTop);
      const left = scrollport ? scrollport.getBoundingClientRect().left + scrollport.clientLeft : 0;
      const width = scrollport?.clientWidth ?? window.innerWidth;
      form.style.setProperty('--drbfm-footer-outset-start', `${Math.max(0, formBox.left - left)}px`);
      form.style.setProperty('--drbfm-footer-outset-end', `${Math.max(0, left + width - formBox.right)}px`);
      form.style.setProperty('--drbfm-input-min-height', `${height}px`);
    };

    update();
    window.addEventListener('resize', update);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    if (form.parentElement) observer?.observe(form.parentElement);
    if (scrollport && scrollport !== form.parentElement) observer?.observe(scrollport);
    return () => {
      window.removeEventListener('resize', update);
      observer?.disconnect();
      form.style.removeProperty('--drbfm-input-min-height');
      form.style.removeProperty('--drbfm-footer-outset-start');
      form.style.removeProperty('--drbfm-footer-outset-end');
    };
  }, [formRef]);
};
